import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import {
  CourseNodeRole,
  CourseNodeType,
  ICourseNode,
  ICourseNodeDAO,
  ICourseNodeEntity,
  ICourseNodeEntityDAO,
  ICourseOut,
} from 'types/domain/course';
import { getEntities } from '../utils/getEntities';
import { parseVerification } from './verifications';
import { getPreferedImageFile } from 'core/sagas/product/assets/parse';
import { IImage, IImageDAO, Resolutions } from 'types/domain/image';
import { parseGroup } from './group';
import { parseVariableDAO } from './variable';
import { parseCourseProgress } from './courseProgress';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.COURSE_NODE]);
const courseId = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.COURSE]);
const groupId = (state: RootState, cId?: number, gId?: number) => (gId ? gId : state.state[EntityTypes.GROUP]);
const moduleId = (state: RootState, cId?: number, mId?: number) => (mId ? mId : state.state[EntityTypes.MODULE]);
export const selectCourseNode = createSelector([entity, id], (entity: IEntityReducer, id: number) => {
  return parseCourseNodeDAO(entity, id);
});

export function parseCourseNodeDAO(entity, id) {
  const courseNode: ICourseNode = entity[EntityTypes.COURSE_NODE][id];

  if (!courseNode) return null;

  const courseOuts = getEntities(entity, EntityTypes.COURSE_OUT, 'sourceId', courseNode.id);

  const avgSessionAttemptTime =
    courseNode.sessionAttemptCount && courseNode.sessionAttemptTotalTime
      ? courseNode.sessionAttemptTotalTime / courseNode.sessionAttemptCount
      : null;

  return {
    ...courseNode,
    avgSessionAttemptTime,
    text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
    image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
    entities: getEntities(entity, EntityTypes.COURSE_NODE_ENTITY, 'courseNodeId', courseNode.id).map(
      courseNodeEntity => {
        return {
          ...courseNodeEntity,
          group: courseNodeEntity.groupId ? entity[EntityTypes.GROUP][courseNodeEntity.groupId] : null,
          outs: courseOuts.filter((out: ICourseOut) => out.sourceHandle === courseNodeEntity.id),
        };
      },
    ),
    courseOuts,

    isDefault: entity[EntityTypes.COURSE][courseNode.courseId]?.courseNodeId === id,
    variable: courseNode.variableId ? entity[EntityTypes.VARIABLE][courseNode.variableId] : null,
    organisationSystem: courseNode.organisationSystemId
      ? entity[EntityTypes.ORG_SYSTEM][courseNode.organisationSystemId]
      : null,
    // verification: courseNode.verificationId
    //   ? parseVerification(entity, courseNode.verificationId, courseNode.id)
    //   : null,
    group: courseNode.groupId ? entity[EntityTypes.GROUP][courseNode.groupId] : null,
    courseNodeGroups: getEntities(entity, EntityTypes.COURSE_NODE_GROUP, 'courseNodeId', courseNode.id).map(
      courseNodeGroup => {
        return {
          ...courseNodeGroup,
          group: courseNodeGroup.groupId ? entity[EntityTypes.GROUP][courseNodeGroup.groupId] : null,
        };
      },
    ),
    courseProgressCurrent: getEntities(entity, EntityTypes.COURSE_PROGRESS, 'courseNodeId', id).map(courseProgress => {
      return parseCourseProgress(entity, courseProgress.id);
    }),
  } as ICourseNodeDAO;
}

export const selectDefaultCourseNode = createSelector(
  [entity, courseId, id],
  (entity: IEntityReducer, courseId: number, courseNodeId) => {
    // if (courseNodeId && entity[EntityTypes.COURSE_NODE][courseNodeId]?.courseId === courseId)
    //   return entity[EntityTypes.COURSE_NODE][courseNodeId];

    if (entity[EntityTypes.COURSE][courseId]?.courseNodeId)
      return entity[EntityTypes.COURSE_NODE][entity[EntityTypes.COURSE][courseId]?.courseNodeId];

    const nodes = getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', courseId);

    if (nodes.length > 0) return nodes[0];
  },
);

export const selectDefaultCourseGroupNode = createSelector(
  [entity, courseId, groupId],
  (entity: IEntityReducer, courseId: number, groupId: number) => {
    return getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', courseId).find(
      (cn: ICourseNode) =>
        cn.type === CourseNodeType.START && cn.entityType === CourseNodeRole.GROUP && cn.groupId === groupId,
    );
  },
);

export const selectDefaultCourseModuleNode = createSelector(
  [entity, courseId, moduleId],
  (entity: IEntityReducer, courseId: number, moduleId: number) => {
    return getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', courseId).find(
      (cn: ICourseNode) => cn.type === CourseNodeType.ASSIGN && cn.moduleId === moduleId,
    );
  },
);

export const selectCourseNodeForModule = createSelector(
  // `id` is not usable here: its second positional argument is this selector's
  // `courseId`, so it would read a course node by the course's id.
  [entity, courseId, moduleId, (state: RootState) => state.state[EntityTypes.COURSE_NODE]],
  (entity: IEntityReducer, courseId: number, moduleId: number, selectedCourseNodeId: number) => {
    // Prefer the currently selected course node if it matches this module
    const selected = entity[EntityTypes.COURSE_NODE][selectedCourseNodeId];
    if (selected?.moduleId === moduleId) return selected as ICourseNode;
    // Fall back to finding the ASSIGN node for this module in the course
    return getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', courseId).find(
      (cn: ICourseNode) => cn.type === CourseNodeType.ASSIGN && cn.moduleId === moduleId && cn.status > -1,
    ) as ICourseNode | undefined;
  },
);

export const selectCourseNodeImageFiles = createSelector([entity, id], (entity: IEntityReducer, courseNodeId) => {
  if (!entity[EntityTypes.COURSE_NODE][courseNodeId]?.imageId) return null;

  const image: IImageDAO = entity[EntityTypes.IMAGE][
    entity[EntityTypes.COURSE_NODE][courseNodeId]?.imageId
  ] as IImageDAO;

  return image?.files;
});

export const selectCourseNodeImage = createSelector([selectCourseNodeImageFiles], files =>
  getPreferedImageFile(files, Resolutions.FULL_2048),
);

export const selectCourseNodeEntities = createSelector(entity, id, (entity: IEntityReducer, courseNodeId) => {
  return getEntities(entity, EntityTypes.COURSE_NODE_ENTITY, 'courseNodeId', courseNodeId).map(
    (courseNodeEntity: ICourseNodeEntity) => {
      return {
        ...courseNodeEntity,
        group: courseNodeEntity.groupId ? parseGroup(courseNodeEntity, courseNodeEntity.groupId) : null,
        variable: courseNodeEntity.variableId ? parseVariableDAO(courseNodeEntity, courseNodeEntity.variableId) : null,
        text: courseNodeEntity.textId ? entity[EntityTypes.TEXT][courseNodeEntity.textId] : null,
      } as ICourseNodeEntityDAO;
    },
  );
});
